import React, { useState, useEffect, useCallback, useRef } from 'react';
import ThemesTab from './ThemesTab';
import SectionsTab from './SectionsTab';
import ContactTab from './ContactTab';
import VersePostTab from './VersePostTab';
import { supabase } from '../lib/supabaseClient';
import PastorsTab from './PastorsTab';
import LoginScreen from './LoginScreen';
import DashboardTab from './DashboardTab';
import ServicesTab from './ServicesTab';
import SocialTab from './SocialTab';

const ADMIN_PASSWORD = ''; // Password handled by API — do not hardcode here

// ─── STYLES ───
const css = {
  page: { minHeight:'100vh', background:'linear-gradient(to br, #f8fafb, #f0f4f8)', fontFamily:'Tahoma,Arial,sans-serif', paddingBottom:80 },
  header: { background:'#ffffff', borderBottom:'1px solid #e5e7eb', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100, boxShadow:'0 1px 3px rgba(0,0,0,0.05)' },
  logo: { fontFamily:"'Cinzel Decorative',serif", fontSize:'0.78rem', color:'#22c55e' },
  cont: { maxWidth:1100, margin:'0 auto', padding:'16px 12px', background:'linear-gradient(to br, #f8fafb, #f0f4f8)' },
  tabs: { display:'none' },
  tab: (a) => ({ padding:'9px 18px', borderRadius:8, border: a?'2px solid #22c55e':'1px solid #e5e7eb', background: a?'#dcfce7':'#ffffff', color: a?'#166534':'#6b7280', fontFamily:'Tahoma,Arial,sans-serif', fontWeight:700, fontSize:'0.82rem', letterSpacing:'0.04em', cursor:'pointer', transition:'all 0.2s' }),
  card: { background:'#ffffff', border:'1px solid #e5e7eb', borderRadius:12, padding:'20px 22px', marginBottom:12, boxShadow:'0 1px 3px rgba(0,0,0,0.05)' },
  input: { width:'100%', padding:'12px 16px', background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:9, color:'#111827', fontFamily:'Tahoma,Arial,sans-serif', fontSize:'0.92rem', outline:'none', marginBottom:0 },
  label: { display:'block', fontSize:'0.72rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'#6b7280', marginBottom:6, fontWeight:'bold' },
  btnGreen: { background:'linear-gradient(135deg,#22c55e,#16a34a)', border:'none', color:'#ffffff', padding:'9px 20px', borderRadius:8, fontSize:'0.74rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.08em', transition:'opacity 0.2s' },
  btnRed: { background:'#fee2e2', border:'1px solid #fecaca', color:'#991b1b', padding:'9px 18px', borderRadius:8, fontSize:'0.74rem', fontWeight:700, cursor:'pointer' },
  btnGold: { background:'linear-gradient(135deg,#f59e0b,#d97706)', border:'none', color:'#ffffff', padding:'10px 24px', borderRadius:9, fontSize:'0.78rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.1em', fontFamily:'Tahoma,Arial,sans-serif' },
  btnGray: { background:'#f3f4f6', border:'1px solid #e5e7eb', color:'#6b7280', padding:'9px 16px', borderRadius:8, fontSize:'0.72rem', cursor:'pointer' },
  pill: (s) => {
    const m = {pending:['#fef3c7','#f59e0b'], approved:['#dcfce7','#22c55e'], rejected:['#fee2e2','#ef4444'], active:['#dcfce7','#22c55e'], inactive:['#f3f4f6','#9ca3af']};
    const [bg,c] = m[s]||m.pending;
    return { background:bg, color:c, border:`1px solid ${c}`, borderRadius:20, padding:'3px 12px', fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase' };
  },
  sectionTitle: { fontFamily:'Tahoma,Arial,sans-serif', fontSize:'1.1rem', fontWeight:'bold', color:'#22c55e', marginBottom:20 },
  statsRow: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:14, marginBottom:28 },
  stat: (c) => ({ background:'#ffffff', border:`2px solid ${c}`, borderRadius:12, padding:'18px 20px', textAlign:'center', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }),
};

// ─── LOGIN ───
// Excel export helper
function exportToExcel(data, filename) {
  const headers = Object.keys(data[0]);
  const rows = data.map(row => headers.map(h => `"${String(row[h]||'').replace(/"/g,'""')}"`).join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], {type:'text/csv'});
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
  a.download = filename + '.csv'; a.click();
}

// ─── TESTIMONIALS TAB ───
function TestimonialsTab() {
  const [items,setItems]=useState([]); const [filter,setFilter]=useState('pending'); const [loading,setLoading]=useState(false);
  const [selected,setSelected]=useState(new Set());
  const fetch=useCallback(async()=>{setLoading(true);const{data}=await supabase.from('testimonials').select('*').order('created_at',{ascending:false});if(data)setItems(data);setLoading(false);},[]);
  useEffect(()=>{fetch();},[fetch]);
  const update=async(id,status)=>{await supabase.from('testimonials').update({status}).eq('id',id);fetch();};
  const del=async(id)=>{if(!window.confirm('Delete?'))return;await supabase.from('testimonials').delete().eq('id',id);fetch();};
  const filtered=filter==='all'?items:items.filter(x=>x.status===filter);
  const counts={pending:items.filter(x=>x.status==='pending').length,approved:items.filter(x=>x.status==='approved').length,rejected:items.filter(x=>x.status==='rejected').length,all:items.length};
  const toggleSelect=(id)=>{setSelected(s=>{const n=new Set(s);n.has(id)?n.delete(id):n.add(id);return n;});};
  const selectAll=()=>setSelected(new Set(filtered.map(x=>x.id)));
  const clearAll=()=>setSelected(new Set());
  const exportSelected=()=>{
    const rows=items.filter(x=>selected.has(x.id)).map(x=>({Name:x.name,Phone:x.phone||'',Location:x.location||'',Testimony:x.testimony,Status:x.status,Date:new Date(x.created_at).toLocaleDateString('en-IN')}));
    if(!rows.length){alert('Select at least one testimony');return;}
    exportToExcel(rows,'testimonials_export');
  };
  const exportAll=()=>{
    const rows=filtered.map(x=>({Name:x.name,Phone:x.phone||'',Location:x.location||'',Testimony:x.testimony,Status:x.status,Date:new Date(x.created_at).toLocaleDateString('en-IN')}));
    exportToExcel(rows,'testimonials_all');
  };
  return (
    <div>
      <div style={css.statsRow}>
        {[['Pending','#f59e0b',counts.pending],['Approved','#22c55e',counts.approved],['Rejected','#ef4444',counts.rejected],['Total','#3b82f6',counts.all]].map(([l,c,n])=>(
          <div key={l} style={css.stat(c+'55')}><div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'2rem',color:c}}>{n}</div><div style={{fontSize:'0.66rem',letterSpacing:'0.12em',textTransform:'uppercase',color:'#6b7280'}}>{l}</div></div>
        ))}
      </div>
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        {['pending','approved','rejected','all'].map(f=>(
          <button key={f} onClick={()=>setFilter(f)} style={{...css.tab(filter===f),padding:'7px 16px',borderRadius:20,textTransform:'uppercase',fontSize:'0.68rem'}}>{f} ({counts[f]})</button>
        ))}
        <button onClick={fetch} style={{...css.btnGray,marginLeft:'auto'}}>🔄 Refresh</button>
      </div>
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <button onClick={selectAll} style={{...css.btnGray,fontSize:'0.72rem'}}>☑ Select All ({filtered.length})</button>
        <button onClick={clearAll} style={{...css.btnGray,fontSize:'0.72rem'}}>✕ Clear</button>
        {selected.size>0&&<button onClick={exportSelected} style={{...css.btnGold,fontSize:'0.72rem',padding:'8px 16px'}}>📥 Export Selected ({selected.size})</button>}
        <button onClick={exportAll} style={{...css.btnGreen,fontSize:'0.72rem'}}>📊 Export All to Excel</button>
      </div>
      {loading&&<div style={{textAlign:'center',color:'#166534',padding:'40px'}}>Loading… 🙏</div>}
      {!loading&&filtered.length===0&&<div style={{textAlign:'center',color:'#6b7280',padding:'40px',fontFamily:"'Crimson Pro',serif",fontSize:'1.05rem'}}>No {filter} testimonials ✝</div>}
      {filtered.map(t=>(
        <div key={t.id} style={{...css.card, border:selected.has(t.id)?'2px solid #166534':'1px solid #e8d8b0'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8,marginBottom:8}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <input type="checkbox" checked={selected.has(t.id)} onChange={()=>toggleSelect(t.id)} style={{width:16,height:16,cursor:'pointer',accentColor:'#166534'}}/>
              <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'0.88rem',color:'#ffffff'}}>{t.name}</div>
            </div>
            <span style={css.pill(t.status)}>{t.status}</span>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:12,fontSize:'0.72rem',color:'#6b7280',marginBottom:10}}>
            {t.phone&&<span>📞 {t.phone}</span>}{t.location&&<span>📍 {t.location}</span>}
            <span>🕐 {new Date(t.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
          </div>
          {/* Editable testimony text */}
          <textarea
            defaultValue={t.testimony}
            onBlur={async(e)=>{
              const newText = e.target.value.trim();
              if(newText && newText !== t.testimony){
                await supabase.from('testimonials').update({testimony:newText}).eq('id',t.id);
                fetch();
              }
            }}
            style={{width:'100%',fontFamily:"'Crimson Pro',serif",fontStyle:'italic',fontSize:'0.97rem',color:'#6b7280',lineHeight:1.65,padding:'12px 16px',background:'#fafaf5',borderRadius:8,borderLeft:'3px solid rgba(201,168,76,0.3)',marginBottom:8,resize:'vertical',border:'1px solid #e8d8b0',outline:'none',minHeight:80}}
          />
          <div style={{fontSize:'0.65rem',color:'#9ca3af',marginBottom:10,fontStyle:'italic'}}>✏️ Click text above to edit before publishing</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {t.status!=='approved'&&<button style={css.btnGreen} onClick={()=>update(t.id,'approved')}>✅ Approve & Publish</button>}
            {t.status!=='rejected'&&<button style={css.btnRed} onClick={()=>update(t.id,'rejected')}>❌ Reject</button>}
            {t.status==='approved'&&<button style={{...css.btnGray,color:'#166534',border:'1px solid rgba(201,168,76,0.3)'}} onClick={()=>update(t.id,'pending')}>↩ Move to Pending</button>}
            <button style={css.btnGray} onClick={()=>del(t.id)}>🗑 Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── PRAYER REQUESTS TAB ───
function PrayerTab() {
  const [items,setItems]=useState([]); const [filter,setFilter]=useState('pending'); const [loading,setLoading]=useState(false);
  const [selected,setSelected]=useState(new Set());
  const fetch=useCallback(async()=>{setLoading(true);const{data}=await supabase.from('prayer_requests').select('*').order('created_at',{ascending:false});if(data)setItems(data);setLoading(false);},[]);
  useEffect(()=>{fetch();},[fetch]);
  const update=async(id,status)=>{await supabase.from('prayer_requests').update({status}).eq('id',id);fetch();};
  const del=async(id)=>{if(!window.confirm('Delete?'))return;await supabase.from('prayer_requests').delete().eq('id',id);fetch();};
  const filtered=filter==='all'?items:items.filter(x=>x.status===filter);
  const counts={pending:items.filter(x=>x.status==='pending').length,approved:items.filter(x=>x.status==='approved').length,all:items.length};
  const toggleSelect=(id)=>{setSelected(s=>{const n=new Set(s);n.has(id)?n.delete(id):n.add(id);return n;});};
  const selectAll=()=>setSelected(new Set(filtered.map(x=>x.id)));
  const clearAll=()=>setSelected(new Set());
  const exportSelected=()=>{
    const rows=items.filter(x=>selected.has(x.id)).map(x=>({Name:x.name,Phone:x.phone||'',Address:x.address||'',Prayer_Request:x.prayer_request,Allow_Publish:x.allow_publish?'Yes':'No',Status:x.status,Date:new Date(x.created_at).toLocaleDateString('en-IN')}));
    if(!rows.length){alert('Select at least one prayer request');return;}
    exportToExcel(rows,'prayer_requests_export');
  };
  const exportAll=()=>{
    const rows=filtered.map(x=>({Name:x.name,Phone:x.phone||'',Address:x.address||'',Prayer_Request:x.prayer_request,Allow_Publish:x.allow_publish?'Yes':'No',Status:x.status,Date:new Date(x.created_at).toLocaleDateString('en-IN')}));
    exportToExcel(rows,'prayer_requests_all');
  };
  return (
    <div>
      <div style={css.statsRow}>
        {[['Pending','#ffa500',counts.pending],['Seen','#5cb85c',counts.approved],['Total','#f59e0b',counts.all]].map(([l,c,n])=>(
          <div key={l} style={css.stat(c+'55')}><div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'2rem',color:c}}>{n}</div><div style={{fontSize:'0.66rem',letterSpacing:'0.12em',textTransform:'uppercase',color:'#6b7280'}}>{l}</div></div>
        ))}
      </div>
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        {['pending','approved','all'].map(f=>(
          <button key={f} onClick={()=>setFilter(f)} style={{...css.tab(filter===f),padding:'7px 16px',borderRadius:20,textTransform:'uppercase',fontSize:'0.68rem'}}>{f==='approved'?'seen':f} ({counts[f]||counts.all})</button>
        ))}
        <button onClick={fetch} style={{...css.btnGray,marginLeft:'auto'}}>🔄 Refresh</button>
      </div>
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <button onClick={selectAll} style={{...css.btnGray,fontSize:'0.72rem'}}>☑ Select All ({filtered.length})</button>
        <button onClick={clearAll} style={{...css.btnGray,fontSize:'0.72rem'}}>✕ Clear</button>
        {selected.size>0&&<button onClick={exportSelected} style={{...css.btnGold,fontSize:'0.72rem',padding:'8px 16px'}}>📥 Export Selected ({selected.size})</button>}
        <button onClick={exportAll} style={{...css.btnGreen,fontSize:'0.72rem'}}>📊 Export All to Excel</button>
      </div>
      {loading&&<div style={{textAlign:'center',color:'#166534',padding:'40px'}}>Loading… 🙏</div>}
      {filtered.map(p=>(
        <div key={p.id} style={css.card}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8,marginBottom:8}}>
            <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'0.88rem',color:'#ffffff'}}>{p.name}</div>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <span style={css.pill(p.status)}>{p.status}</span>
              {p.allow_publish&&<span style={{background:'rgba(201,168,76,0.12)',color:'#166534',border:'1px solid rgba(201,168,76,0.3)',borderRadius:20,padding:'3px 10px',fontSize:'0.65rem',fontWeight:700}}>📢 Public</span>}
            </div>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:12,fontSize:'0.72rem',color:'#6b7280',marginBottom:10}}>
            {p.phone&&<span>📞 {p.phone}</span>}{p.address&&<span>📍 {p.address}</span>}
            <span>🕐 {new Date(p.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
          </div>
          <div style={{fontFamily:"'Crimson Pro',serif",fontStyle:'italic',fontSize:'0.97rem',color:'#6b7280',lineHeight:1.65,padding:'12px 16px',background:'#fafaf5',borderRadius:8,borderLeft:'3px solid rgba(201,168,76,0.3)',marginBottom:14}}>{p.prayer_request}</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {p.status==='pending'&&<button style={css.btnGreen} onClick={()=>update(p.id,'approved')}>✅ Mark as Seen</button>}
            <button style={css.btnGray} onClick={()=>del(p.id)}>🗑 Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── GALLERY TAB ───
function GalleryTab() {
  const [images,setImages]=useState([]); const [uploading,setUploading]=useState(false); const [loading,setLoading]=useState(false);
  const inputRef=useRef();
  const fetch=useCallback(async()=>{
    setLoading(true);
    const{data}=await supabase.storage.from('gallery').list('',{sortBy:{column:'created_at',order:'desc'}});
    if(data){setImages(data.filter(f=>f.name!=='.emptyFolderPlaceholder').map(f=>({...f,url:supabase.storage.from('gallery').getPublicUrl(f.name).data.publicUrl})));}
    setLoading(false);
  },[]);
  useEffect(()=>{fetch();},[fetch]);

  const upload=async(e)=>{
    const files=Array.from(e.target.files);
    if(!files.length)return;
    setUploading(true);
    for(const file of files){
      const name=`${Date.now()}_${file.name}`;
      await supabase.storage.from('gallery').upload(name,file,{contentType:file.type});
    }
    setUploading(false);
    fetch();
    e.target.value='';
  };

  const del=async(name)=>{
    if(!window.confirm('Delete this photo?'))return;
    await supabase.storage.from('gallery').remove([name]);
    fetch();
  };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24,flexWrap:'wrap',gap:12}}>
        <div style={{color:'#6b7280',fontSize:'0.82rem'}}>{images.length} photo{images.length!==1?'s':''} in gallery</div>
        <div style={{display:'flex',gap:10}}>
          <button onClick={fetch} style={css.btnGray}>🔄 Refresh</button>
          <label htmlFor="galUpload" style={{...css.btnGold,cursor:'pointer',display:'inline-block'}}>
            {uploading?'⏳ Uploading…':'📤 Upload Photos'}
          </label>
          <input id="galUpload" ref={inputRef} type="file" multiple accept="image/*" style={{display:'none'}} onChange={upload}/>
        </div>
      </div>
      {loading&&<div style={{textAlign:'center',color:'#166534',padding:'40px'}}>Loading… 🙏</div>}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:14}}>
        {images.map(img=>(
          <div key={img.name} style={{borderRadius:12,overflow:'hidden',border:'1px solid rgba(34, 197, 94, 0.2)',position:'relative',aspectRatio:'4/3',background:'rgba(13,27,62,0.8)'}}>
            <img src={img.url} alt={img.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
            <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,0)',transition:'background 0.3s',display:'flex',alignItems:'center',justifyContent:'center'}}
              onMouseEnter={e=>{e.currentTarget.style.background='rgba(0,0,0,0.5)';e.currentTarget.querySelector('button').style.opacity='1';}}
              onMouseLeave={e=>{e.currentTarget.style.background='rgba(0,0,0,0)';e.currentTarget.querySelector('button').style.opacity='0';}}>
              <button onClick={()=>del(img.name)} style={{opacity:0,background:'rgba(178,34,34,0.9)',border:'none',color:'#ffffff',padding:'8px 18px',borderRadius:8,cursor:'pointer',transition:'opacity 0.3s',fontSize:'0.8rem'}}>🗑 Delete</button>
            </div>
          </div>
        ))}
        {images.length===0&&!loading&&(
          <div style={{gridColumn:'1/-1',textAlign:'center',padding:'60px',color:'#6b7280',fontFamily:"'Crimson Pro',serif",fontSize:'1.05rem'}}>
            No photos yet. Click "Upload Photos" to add your first photo! 📸
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DAILY VERSE TAB ───
function VerseTab() {
  const [verses,setVerses]=useState([]); const [loading,setLoading]=useState(false);
  const [showForm,setShowForm]=useState(false);
  const [editItem,setEditItem]=useState(null);
  const [form,setForm]=useState({english_text:'',english_ref:'',tamil_text:'',tamil_ref:'',malayalam_text:'',malayalam_ref:'',scheduled_date:''});
  const [saving,setSaving]=useState(false);
  const [csvMode,setCsvMode]=useState(false);
  const [csvText,setCsvText]=useState('');
  const fileRef=useRef();

  const fetch=useCallback(async()=>{setLoading(true);const{data}=await supabase.from('daily_verses').select('*').order('scheduled_date',{ascending:true});if(data)setVerses(data);setLoading(false);},[]);
  useEffect(()=>{fetch();},[fetch]);

  const save=async()=>{
    if(!form.english_text||!form.english_ref||!form.scheduled_date){alert('Please fill all required fields');return;}
    setSaving(true);
    if(editItem){await supabase.from('daily_verses').update(form).eq('id',editItem.id);}
    else{await supabase.from('daily_verses').insert([form]);}
    setSaving(false);setShowForm(false);setEditItem(null);setForm({english_text:'',english_ref:'',tamil_text:'',tamil_ref:'',malayalam_text:'',malayalam_ref:'',scheduled_date:''});
    fetch();
  };

  const del=async(id)=>{if(!window.confirm('Delete?'))return;await supabase.from('daily_verses').delete().eq('id',id);fetch();};

  const startEdit=(v)=>{setEditItem(v);setForm({english_text:v.english_text,english_ref:v.english_ref,tamil_text:v.tamil_text||'',tamil_ref:v.tamil_ref||'',malayalam_text:v.malayalam_text||'',malayalam_ref:v.malayalam_ref||'',scheduled_date:v.scheduled_date});setShowForm(true);};

  // Excel/CSV download template
  const downloadTemplate=()=>{
    const rows=['Date (YYYY-MM-DD),English Text,English Ref,Tamil Text,Tamil Ref,Malayalam Text,Malayalam Ref'];
    for(let i=1;i<=31;i++){const d=new Date(2026,3,i);const ds=d.toISOString().split('T')[0];rows.push(`${ds},English verse here,John 3:16,Tamil verse here,யோவான் 3:16,Malayalam verse here,യോഹന്നാൻ 3:16`);}
    const blob=new Blob([rows.join('\n')],{type:'text/csv'});
    const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download='zion_verses_template.csv';a.click();
  };

  // CSV bulk import
  const handleCSVFile=async(e)=>{
    const file=e.target.files[0];if(!file)return;
    const text=await file.text();
    const lines=text.trim().split('\n').slice(1);
    const rows=lines.map(l=>{const p=l.split(',');return{scheduled_date:p[0]?.trim(),english_text:p[1]?.trim(),english_ref:p[2]?.trim(),tamil_text:p[3]?.trim()||'',tamil_ref:p[4]?.trim()||'',malayalam_text:p[5]?.trim()||'',malayalam_ref:p[6]?.trim()||''};}).filter(r=>r.scheduled_date&&r.english_text);
    if(rows.length===0){alert('No valid rows found. Please use the template.');return;}
    if(!window.confirm(`Import ${rows.length} verses?`))return;
    setSaving(true);
    await supabase.from('daily_verses').upsert(rows,{onConflict:'scheduled_date'});
    setSaving(false);fetch();alert(`✅ ${rows.length} verses imported successfully!`);
    e.target.value='';
  };

  const today=new Date().toISOString().split('T')[0];

  return (
    <div>
      {/* Actions */}
      <div style={{display:'flex',gap:10,marginBottom:24,flexWrap:'wrap',alignItems:'center'}}>
        <button style={css.btnGold} onClick={()=>{setShowForm(true);setEditItem(null);}}>➕ Add Verse</button>
        <button style={css.btnGray} onClick={downloadTemplate}>📥 Download CSV Template</button>
        <label style={{...css.btnGold,cursor:'pointer',background:'linear-gradient(135deg,#1a5c1a,#0d3d0d)'}}>
          {saving?'⏳ Importing…':'📤 Import CSV (Bulk)'}
          <input type="file" accept=".csv" style={{display:'none'}} onChange={handleCSVFile}/>
        </label>
        <button onClick={fetch} style={css.btnGray}>🔄 Refresh</button>
        <div style={{marginLeft:'auto',fontSize:'0.75rem',color:'#6b7280'}}>{verses.length} verses scheduled</div>
      </div>

      {/* CSV tip */}
      <div style={{background:'rgba(34, 197, 94, 0.08)',border:'1px solid rgba(34, 197, 94, 0.2)',borderRadius:10,padding:'12px 16px',marginBottom:20,fontSize:'0.78rem',color:'#6b7280',lineHeight:1.6}}>
        💡 <strong style={{color:'#166534'}}>Bulk upload tip:</strong> Download the CSV template → fill in your 31 verses → upload it back. All verses will be saved at once!
      </div>

      {/* Add/Edit Form */}
      {showForm&&(
        <div style={{background:'##f9fafb',border:'1px solid rgba(201,168,76,0.3)',borderRadius:14,padding:'28px 24px',marginBottom:24}}>
          <h3 style={css.sectionTitle}>{editItem?'✏️ Edit Verse':'➕ Add New Verse'}</h3>
          <div style={{marginBottom:14}}>
            <label style={css.label}>Date * (which day this verse shows)</label>
            <input type="date" value={form.scheduled_date} onChange={e=>setForm(f=>({...f,scheduled_date:e.target.value}))} style={css.input}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
            <div><label style={css.label}>English Verse *</label><textarea value={form.english_text} onChange={e=>setForm(f=>({...f,english_text:e.target.value}))} style={{...css.input,height:90,resize:'vertical'}} placeholder="English verse text"/></div>
            <div><label style={css.label}>English Reference *</label><input value={form.english_ref} onChange={e=>setForm(f=>({...f,english_ref:e.target.value}))} style={css.input} placeholder="e.g. John 3:16"/></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
            <div><label style={css.label}>Tamil Verse</label><textarea value={form.tamil_text} onChange={e=>setForm(f=>({...f,tamil_text:e.target.value}))} style={{...css.input,height:90,resize:'vertical'}} placeholder="Tamil verse text"/></div>
            <div><label style={css.label}>Tamil Reference</label><input value={form.tamil_ref} onChange={e=>setForm(f=>({...f,tamil_ref:e.target.value}))} style={css.input} placeholder="e.g. யோவான் 3:16"/></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:18}}>
            <div><label style={css.label}>Malayalam Verse</label><textarea value={form.malayalam_text} onChange={e=>setForm(f=>({...f,malayalam_text:e.target.value}))} style={{...css.input,height:90,resize:'vertical'}} placeholder="Malayalam verse text"/></div>
            <div><label style={css.label}>Malayalam Reference</label><input value={form.malayalam_ref} onChange={e=>setForm(f=>({...f,malayalam_ref:e.target.value}))} style={css.input} placeholder="e.g. യോഹന്നാൻ 3:16"/></div>
          </div>
          <div style={{display:'flex',gap:10}}>
            <button style={css.btnGold} onClick={save} disabled={saving}>{saving?'⏳ Saving…':'💾 Save Verse'}</button>
            <button style={css.btnGray} onClick={()=>{setShowForm(false);setEditItem(null);}}>Cancel</button>
          </div>
        </div>
      )}

      {loading&&<div style={{textAlign:'center',color:'#166534',padding:'40px'}}>Loading… 🙏</div>}
      {verses.map(v=>(
        <div key={v.id} style={{...css.card,borderLeft:v.scheduled_date===today?'3px solid #f59e0b':'1px solid rgba(201,168,76,0.18)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8,marginBottom:10}}>
            <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'0.82rem',color:'#166534'}}>
              📅 {new Date(v.scheduled_date+'T00:00:00').toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'long',year:'numeric'})}
              {v.scheduled_date===today&&<span style={{...css.pill('approved'),marginLeft:10}}>TODAY</span>}
            </div>
            <div style={{display:'flex',gap:8}}>
              <button style={{...css.btnGray,color:'#166534',border:'1px solid rgba(201,168,76,0.3)'}} onClick={()=>startEdit(v)}>✏️ Edit</button>
              <button style={css.btnGray} onClick={()=>del(v.id)}>🗑</button>
            </div>
          </div>
          <p style={{fontFamily:"'Crimson Pro',serif",fontStyle:'italic',fontSize:'0.92rem',color:'#6b7280',marginBottom:4}}>🇬🇧 "{v.english_text}" — {v.english_ref}</p>
          {v.tamil_text&&<p style={{fontFamily:"'Crimson Pro',serif",fontStyle:'italic',fontSize:'0.88rem',color:'#6b7280',marginBottom:4}}>🕊️ "{v.tamil_text}" — {v.tamil_ref}</p>}
          {v.malayalam_text&&<p style={{fontFamily:"'Crimson Pro',serif",fontStyle:'italic',fontSize:'0.88rem',color:'#6b7280'}}>✝️ "{v.malayalam_text}" — {v.malayalam_ref}</p>}
        </div>
      ))}
      {verses.length===0&&!loading&&<div style={{textAlign:'center',color:'#6b7280',padding:'40px',fontFamily:"'Crimson Pro',serif",fontSize:'1.05rem'}}>No verses scheduled yet. Add one or bulk import CSV! 📖</div>}
    </div>
  );
}

// ─── EVENTS TAB ───
function EventsTab() {
  const [items,setItems]=useState([]); const [loading,setLoading]=useState(false);
  const [showForm,setShowForm]=useState(false); const [editItem,setEditItem]=useState(null);
  const [form,setForm]=useState({title:'',event_date:'',event_time:'',timings:[''],description:'',icon:'✝'}); const [saving,setSaving]=useState(false);
  const fetch=useCallback(async()=>{setLoading(true);const{data}=await supabase.from('events').select('*').order('event_date',{ascending:true});if(data)setItems(data);setLoading(false);},[]);
  useEffect(()=>{fetch();},[fetch]);
  const save=async()=>{if(!form.title||!form.event_date){alert('Title and date required');return;}setSaving(true);
    const payload={...form,event_time:(form.timings||[]).filter(Boolean).join(', ')};
    delete payload.timings;
    if(editItem){await supabase.from('events').update(payload).eq('id',editItem.id);}else{await supabase.from('events').insert([payload]);}setSaving(false);setShowForm(false);setEditItem(null);setForm({title:'',event_date:'',event_time:'',timings:[''],description:'',icon:'✝'});fetch();};
  const del=async(id)=>{if(!window.confirm('Delete?'))return;await supabase.from('events').delete().eq('id',id);fetch();};
  const startEdit=(e)=>{setEditItem(e);setForm({title:e.title,event_date:e.event_date,event_time:e.event_time||'',timings:e.event_time?e.event_time.split(', '):[''],description:e.description||'',icon:e.icon||'✝'});setShowForm(true);};
  const icons=['✝','🌅','🎄','🌟','🎊','🕊️','🔥','⭐','🙏','🎉','🎵','📖'];
  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:24,flexWrap:'wrap'}}>
        <button style={css.btnGold} onClick={()=>{setShowForm(true);setEditItem(null);}}>➕ Add Event</button>
        <button onClick={fetch} style={css.btnGray}>🔄 Refresh</button>
      </div>
      {showForm&&(
        <div style={{background:'##f9fafb',border:'1px solid rgba(201,168,76,0.3)',borderRadius:14,padding:'28px 24px',marginBottom:24}}>
          <h3 style={css.sectionTitle}>{editItem?'✏️ Edit Event':'➕ Add New Event'}</h3>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:14,marginBottom:14}}>
            <div><label style={css.label}>Event Title *</label><input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={css.input} placeholder="e.g. Good Friday Service"/></div>
            <div><label style={css.label}>Event Date *</label><input type="date" value={form.event_date} onChange={e=>setForm(f=>({...f,event_date:e.target.value}))} style={css.input}/></div>
            <div><label style={css.label}>Event Time</label><input type="time" value={form.event_time} onChange={e=>setForm(f=>({...f,event_time:e.target.value}))} style={css.input}/></div>
          </div>
          <div style={{marginBottom:14}}><label style={css.label}>Description</label><textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={{...css.input,height:90,resize:'vertical'}} placeholder="Event description…"/></div>
          <div style={{marginBottom:14}}>
            <label style={css.label}>Event Timings (can add multiple)</label>
            {(form.timings||['']).map((t,i)=>(
              <div key={i} style={{display:'flex',gap:8,marginBottom:8,alignItems:'center'}}>
                <input type="time" value={t} onChange={e=>{const ts=[...(form.timings||[''])];ts[i]=e.target.value;setForm(f=>({...f,timings:ts}));}} style={{...css.input,width:160,marginBottom:0}}/>
                {i===0
                  ? <button type="button" onClick={()=>setForm(f=>({...f,timings:[...(f.timings||['']),'']})) } style={{...css.btnGold,padding:'8px 14px',fontSize:'0.78rem'}}>+ Add Time</button>
                  : <button type="button" onClick={()=>{const ts=(form.timings||['']).filter((_,j)=>j!==i);setForm(f=>({...f,timings:ts}));}} style={{...css.btnRed,padding:'8px 14px',fontSize:'0.78rem'}}>✕</button>
                }
              </div>
            ))}
          </div>
          <div style={{marginBottom:18}}>
            <label style={css.label}>Icon / Emoji</label>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:4}}>
              {icons.map(ic=>(
                <button key={ic} onClick={()=>setForm(f=>({...f,icon:ic}))} style={{width:40,height:40,borderRadius:8,border:`2px solid ${form.icon===ic?'#f59e0b':'rgba(34, 197, 94, 0.2)'}`,background:form.icon===ic?'rgba(34, 197, 94, 0.2)':'transparent',fontSize:'1.3rem',cursor:'pointer'}}>{ic}</button>
              ))}
            </div>
          </div>
          <div style={{display:'flex',gap:10}}>
            <button style={css.btnGold} onClick={save} disabled={saving}>{saving?'⏳ Saving…':'💾 Save Event'}</button>
            <button style={css.btnGray} onClick={()=>{setShowForm(false);setEditItem(null);}}>Cancel</button>
          </div>
        </div>
      )}
      {loading&&<div style={{textAlign:'center',color:'#166534',padding:'40px'}}>Loading… 🙏</div>}
      {items.map(ev=>(
        <div key={ev.id} style={css.card}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8,marginBottom:8}}>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <span style={{fontSize:'2rem'}}>{ev.icon}</span>
              <div>
                <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'0.88rem',color:'#166534'}}>{ev.title}</div>
                <div style={{fontSize:'0.72rem',color:'#6b7280',marginTop:3}}>📅 {new Date(ev.event_date+'T00:00:00').toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}{ev.event_time ? ` · 🕐 ${ev.event_time}` : ''}</div>
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button style={{...css.btnGray,color:'#166534',border:'1px solid rgba(201,168,76,0.3)'}} onClick={()=>startEdit(ev)}>✏️ Edit</button>
              <button style={css.btnGray} onClick={()=>del(ev.id)}>🗑</button>
            </div>
          </div>
          {ev.description&&<p style={{fontFamily:"'Crimson Pro',serif",fontSize:'0.92rem',color:'#6b7280',lineHeight:1.6,paddingLeft:52}}>{ev.description}</p>}
        </div>
      ))}
      {items.length===0&&!loading&&<div style={{textAlign:'center',color:'#6b7280',padding:'40px',fontFamily:"'Crimson Pro',serif",fontSize:'1.05rem'}}>No events yet. Add your first event! 📅</div>}
    </div>
  );
}

// ─── SERMONS TAB ───
function SermonsTab() {
  const [items,setItems]=useState([]); const [loading,setLoading]=useState(false);
  const [showForm,setShowForm]=useState(false); const [editItem,setEditItem]=useState(null);
  const [form,setForm]=useState({title:'',preacher:'',youtube_url:'',verse:'',description:'',published:true}); const [saving,setSaving]=useState(false);
  const fetch=useCallback(async()=>{setLoading(true);const{data}=await supabase.from('sermons').select('*').order('created_at',{ascending:false});if(data)setItems(data);setLoading(false);},[]);
  useEffect(()=>{fetch();},[fetch]);
  const getVideoId=(url)=>{const m=url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([^?&\s]+)/);return m?m[1]:null;};
  const save=async()=>{if(!form.title||!form.preacher||!form.youtube_url){alert('Title, preacher and YouTube URL required');return;}setSaving(true);if(editItem){await supabase.from('sermons').update(form).eq('id',editItem.id);}else{await supabase.from('sermons').insert([form]);}setSaving(false);setShowForm(false);setEditItem(null);setForm({title:'',preacher:'',youtube_url:'',verse:'',description:'',published:true});fetch();};
  const del=async(id)=>{if(!window.confirm('Delete?'))return;await supabase.from('sermons').delete().eq('id',id);fetch();};
  const togglePublish=async(id,val)=>{await supabase.from('sermons').update({published:val}).eq('id',id);fetch();};
  const startEdit=(s)=>{setEditItem(s);setForm({title:s.title,preacher:s.preacher,youtube_url:s.youtube_url,verse:s.verse||'',description:s.description||'',published:s.published});setShowForm(true);};
  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:24}}>
        <button style={css.btnGold} onClick={()=>{setShowForm(true);setEditItem(null);}}>➕ Add Sermon</button>
        <button onClick={fetch} style={css.btnGray}>🔄 Refresh</button>
      </div>
      {showForm&&(
        <div style={{background:'##f9fafb',border:'1px solid rgba(201,168,76,0.3)',borderRadius:14,padding:'28px 24px',marginBottom:24}}>
          <h3 style={css.sectionTitle}>{editItem?'✏️ Edit Sermon':'➕ Add New Sermon'}</h3>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
            <div><label style={css.label}>Sermon Title *</label><input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={css.input} placeholder="Sermon title"/></div>
            <div><label style={css.label}>Preacher *</label><input value={form.preacher} onChange={e=>setForm(f=>({...f,preacher:e.target.value}))} style={css.input} placeholder="e.g. Pastor Philip Rajkumar"/></div>
          </div>
          <div style={{marginBottom:14}}><label style={css.label}>YouTube URL *</label><input value={form.youtube_url} onChange={e=>setForm(f=>({...f,youtube_url:e.target.value}))} style={css.input} placeholder="https://youtu.be/..."/></div>
          {form.youtube_url&&getVideoId(form.youtube_url)&&(
            <div style={{marginBottom:14}}>
              <img src={`https://img.youtube.com/vi/${getVideoId(form.youtube_url)}/hqdefault.jpg`} alt="thumbnail" style={{height:100,borderRadius:8,border:'1px solid rgba(201,168,76,0.3)'}}/>
            </div>
          )}
          <div style={{marginBottom:14}}><label style={css.label}>Bible Verse (Optional)</label><input value={form.verse} onChange={e=>setForm(f=>({...f,verse:e.target.value}))} style={css.input} placeholder='e.g. "I can do all things…" — Phil 4:13'/></div>
          <div style={{marginBottom:14}}><label style={css.label}>Description (Optional)</label><textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={{...css.input,height:80,resize:'vertical'}} placeholder="Brief sermon description…"/></div>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:18,cursor:'pointer'}} onClick={()=>setForm(f=>({...f,published:!f.published}))}>
            <div style={{width:40,height:22,borderRadius:22,background:form.published?'#f59e0b':'rgba(255,255,255,0.12)',position:'relative',transition:'background 0.3s',flexShrink:0}}>
              <div style={{position:'absolute',top:3,left:form.published?21:3,width:16,height:16,borderRadius:'50%',background:form.published?'#070f22':'#c8bfa8',transition:'left 0.3s'}}/>
            </div>
            <span style={{fontSize:'0.82rem',color:'#6b7280'}}>Published (shows on website)</span>
          </div>
          <div style={{display:'flex',gap:10}}>
            <button style={css.btnGold} onClick={save} disabled={saving}>{saving?'⏳ Saving…':'💾 Save Sermon'}</button>
            <button style={css.btnGray} onClick={()=>{setShowForm(false);setEditItem(null);}}>Cancel</button>
          </div>
        </div>
      )}
      {loading&&<div style={{textAlign:'center',color:'#166534',padding:'40px'}}>Loading… 🙏</div>}
      {items.map(s=>{const vid=getVideoId(s.youtube_url);return(
        <div key={s.id} style={css.card}>
          <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
            {vid&&<img src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`} alt="" style={{width:120,height:68,objectFit:'cover',borderRadius:8,flexShrink:0}}/>}
            <div style={{flex:1,minWidth:200}}>
              <div style={{display:'flex',justifyContent:'space-between',flexWrap:'wrap',gap:8,marginBottom:6}}>
                <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'0.85rem',color:'#166534'}}>{s.title}</div>
                <span style={css.pill(s.published?'approved':'rejected')}>{s.published?'Published':'Hidden'}</span>
              </div>
              <div style={{fontSize:'0.72rem',color:'#6b7280',marginBottom:6}}>✝ {s.preacher}</div>
              {s.verse&&<p style={{fontFamily:"'Crimson Pro',serif",fontStyle:'italic',fontSize:'0.85rem',color:'#6b7280'}}>{s.verse}</p>}
            </div>
          </div>
          <div style={{display:'flex',gap:8,marginTop:12,flexWrap:'wrap'}}>
            <button style={{...css.btnGray,color:'#166534',border:'1px solid rgba(201,168,76,0.3)'}} onClick={()=>startEdit(s)}>✏️ Edit</button>
            <button style={s.published?css.btnRed:css.btnGreen} onClick={()=>togglePublish(s.id,!s.published)}>{s.published?'🚫 Hide':'✅ Publish'}</button>
            <button style={css.btnGray} onClick={()=>del(s.id)}>🗑</button>
          </div>
        </div>
      );})}
      {items.length===0&&!loading&&<div style={{textAlign:'center',color:'#6b7280',padding:'40px',fontFamily:"'Crimson Pro',serif",fontSize:'1.05rem'}}>No sermons yet. Add your first sermon! 🎤</div>}
    </div>
  );
}

// ─── ANNOUNCEMENTS TAB ───
function AnnouncementsTab() {
  const [items,setItems]=useState([]); const [loading,setLoading]=useState(false);
  const [showForm,setShowForm]=useState(false);
  const [form,setForm]=useState({title:'',message:'',timings:[''],active:true}); const [saving,setSaving]=useState(false);
  const fetch=useCallback(async()=>{setLoading(true);const{data}=await supabase.from('announcements').select('*').order('created_at',{ascending:false});if(data)setItems(data);setLoading(false);},[]);
  useEffect(()=>{fetch();},[fetch]);
  const save=async()=>{
    if(!form.title||!form.message){alert('Title and message required');return;}
    setSaving(true);
    const payload={...form, event_time:(form.timings||[]).filter(Boolean).join(', ')};
    delete payload.timings;
    await supabase.from('announcements').insert([payload]);
    setSaving(false);setShowForm(false);setForm({title:'',message:'',timings:[''],active:true});fetch();
  };
  const del=async(id)=>{if(!window.confirm('Delete?'))return;await supabase.from('announcements').delete().eq('id',id);fetch();};
  const toggle=async(id,val)=>{await supabase.from('announcements').update({active:val}).eq('id',id);fetch();};
  return (
    <div>
      <div style={{background:'rgba(34, 197, 94, 0.08)',border:'1px solid rgba(34, 197, 94, 0.2)',borderRadius:10,padding:'12px 16px',marginBottom:20,fontSize:'0.78rem',color:'#6b7280',lineHeight:1.6}}>
        📢 <strong style={{color:'#166534'}}>How it works:</strong> Active announcements show as a popup when visitors open the website. Turn off to hide without deleting.
      </div>
      <div style={{display:'flex',gap:10,marginBottom:24}}>
        <button style={css.btnGold} onClick={()=>setShowForm(true)}>➕ New Announcement</button>
        <button onClick={fetch} style={css.btnGray}>🔄 Refresh</button>
      </div>
      {showForm&&(
        <div style={{background:'##f9fafb',border:'1px solid rgba(201,168,76,0.3)',borderRadius:14,padding:'28px 24px',marginBottom:24}}>
          <h3 style={css.sectionTitle}>📢 New Announcement</h3>
          <div style={{marginBottom:14}}><label style={css.label}>Title *</label><input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={css.input} placeholder="e.g. Special Prayer Meeting This Sunday!"/></div>
          <div style={{marginBottom:14}}><label style={css.label}>Message *</label><textarea value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))} style={{...css.input,height:100,resize:'vertical'}} placeholder="Announcement details…"/></div>
          <div style={{marginBottom:14}}>
            <label style={css.label}>Timings (Optional — add multiple)</label>
            {(form.timings||['']).map((t,i)=>(
              <div key={i} style={{display:'flex',gap:8,marginBottom:8,alignItems:'center'}}>
                <input type="time" value={t} onChange={e=>{const ts=[...(form.timings||[''])];ts[i]=e.target.value;setForm(f=>({...f,timings:ts}));}} style={{...css.input,width:160,marginBottom:0}}/>
                {i===0
                  ? <button type="button" onClick={()=>setForm(f=>({...f,timings:[...(f.timings||['']),'']})) } style={{...css.btnGold,padding:'8px 14px',fontSize:'0.78rem'}}>+ Add Time</button>
                  : <button type="button" onClick={()=>{const ts=(form.timings||['']).filter((_,j)=>j!==i);setForm(f=>({...f,timings:ts}));}} style={{...css.btnRed,padding:'8px 14px',fontSize:'0.78rem'}}>✕</button>
                }
              </div>
            ))}
          </div>

          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:18,cursor:'pointer'}} onClick={()=>setForm(f=>({...f,active:!f.active}))}>
            <div style={{width:40,height:22,borderRadius:22,background:form.active?'#f59e0b':'rgba(255,255,255,0.12)',position:'relative',transition:'background 0.3s',flexShrink:0}}>
              <div style={{position:'absolute',top:3,left:form.active?21:3,width:16,height:16,borderRadius:'50%',background:form.active?'#070f22':'#c8bfa8',transition:'left 0.3s'}}/>
            </div>
            <span style={{fontSize:'0.82rem',color:'#6b7280'}}>Show as popup on website immediately</span>
          </div>
          <div style={{display:'flex',gap:10}}>
            <button style={css.btnGold} onClick={save} disabled={saving}>{saving?'⏳ Saving…':'💾 Post Announcement'}</button>
            <button style={css.btnGray} onClick={()=>setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}
      {loading&&<div style={{textAlign:'center',color:'#166534',padding:'40px'}}>Loading… 🙏</div>}
      {items.map(a=>(
        <div key={a.id} style={css.card}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8,marginBottom:8}}>
            <div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'0.88rem',color:'#166534'}}>{a.title}</div>
            {a.event_time && <div style={{fontSize:'0.72rem',color:'#166534',marginTop:4}}>🕐 {a.event_time}</div>}
            <span style={css.pill(a.active?'active':'inactive')}>{a.active?'Active':'Inactive'}</span>
          </div>
          <p style={{fontFamily:"'Crimson Pro',serif",fontSize:'0.95rem',color:'#6b7280',lineHeight:1.6,marginBottom:14}}>{a.message}</p>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <button style={a.active?css.btnRed:css.btnGreen} onClick={()=>toggle(a.id,!a.active)}>{a.active?'🚫 Deactivate':'✅ Activate'}</button>
            <button style={css.btnGray} onClick={()=>del(a.id)}>🗑 Delete</button>
          </div>
        </div>
      ))}
      {items.length===0&&!loading&&<div style={{textAlign:'center',color:'#6b7280',padding:'40px',fontFamily:"'Crimson Pro',serif",fontSize:'1.05rem'}}>No announcements. Post one to notify your congregation! 📢</div>}
    </div>
  );
}

// ─── BRANCHES TAB ───
function BranchesTab() {
  const [items,setItems]=useState([]); const [loading,setLoading]=useState(false);
  const [showForm,setShowForm]=useState(false); const [editItem,setEditItem]=useState(null);
  const [form,setForm]=useState({location:'',pastor:'',address:'',phone:'',display_order:0}); const [saving,setSaving]=useState(false);
  const fetch=useCallback(async()=>{setLoading(true);const{data}=await supabase.from('branches').select('*').order('display_order',{ascending:true});if(data)setItems(data);setLoading(false);},[]);
  useEffect(()=>{fetch();},[fetch]);
  const save=async()=>{if(!form.location||!form.pastor){alert('Location and pastor required');return;}setSaving(true);if(editItem){await supabase.from('branches').update(form).eq('id',editItem.id);}else{await supabase.from('branches').insert([form]);}setSaving(false);setShowForm(false);setEditItem(null);setForm({location:'',pastor:'',address:'',phone:'',display_order:0});fetch();};
  const del=async(id)=>{if(!window.confirm('Delete?'))return;await supabase.from('branches').delete().eq('id',id);fetch();};
  const startEdit=(b)=>{setEditItem(b);setForm({location:b.location,pastor:b.pastor,address:b.address||'',phone:b.phone||'',display_order:b.display_order||0});setShowForm(true);};
  return (
    <div>
      <div style={{display:'flex',gap:10,marginBottom:24}}>
        <button style={css.btnGold} onClick={()=>{setShowForm(true);setEditItem(null);}}>➕ Add Branch</button>
        <button onClick={fetch} style={css.btnGray}>🔄 Refresh</button>
      </div>
      {showForm&&(
        <div style={{background:'##f9fafb',border:'1px solid rgba(201,168,76,0.3)',borderRadius:14,padding:'28px 24px',marginBottom:24}}>
          <h3 style={css.sectionTitle}>{editItem?'✏️ Edit Branch':'➕ Add Branch Church'}</h3>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
            <div><label style={css.label}>Location *</label><input value={form.location} onChange={e=>setForm(f=>({...f,location:e.target.value}))} style={css.input} placeholder="e.g. Kandhal"/></div>
            <div><label style={css.label}>Pastor Name *</label><input value={form.pastor} onChange={e=>setForm(f=>({...f,pastor:e.target.value}))} style={css.input} placeholder="e.g. Jeyakumar"/></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:18}}>
            <div><label style={css.label}>Address (Optional)</label><input value={form.address} onChange={e=>setForm(f=>({...f,address:e.target.value}))} style={css.input} placeholder="Street address"/></div>
            <div><label style={css.label}>Phone (Optional)</label><input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} style={css.input} placeholder="+91 XXXXX XXXXX"/></div>
          </div>
          <div style={{display:'flex',gap:10}}>
            <button style={css.btnGold} onClick={save} disabled={saving}>{saving?'⏳ Saving…':'💾 Save'}</button>
            <button style={css.btnGray} onClick={()=>{setShowForm(false);setEditItem(null);}}>Cancel</button>
          </div>
        </div>
      )}
      {loading&&<div style={{textAlign:'center',color:'#166534',padding:'40px'}}>Loading… 🙏</div>}
      {items.map(b=>(
        <div key={b.id} style={css.card}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8}}>
            <div><div style={{fontFamily:"'Cinzel Decorative',serif",fontSize:'0.88rem',color:'#166534'}}>⛪ {b.location}</div>
              <div style={{fontSize:'0.78rem',color:'#6b7280',marginTop:4}}>Pastor {b.pastor}</div>
              {b.address&&<div style={{fontSize:'0.72rem',color:'rgba(200,191,168,0.6)',marginTop:2}}>📍 {b.address}</div>}
              {b.phone&&<div style={{fontSize:'0.72rem',color:'rgba(200,191,168,0.6)',marginTop:2}}>📞 {b.phone}</div>}
            </div>
            <div style={{display:'flex',gap:8}}>
              <button style={{...css.btnGray,color:'#166534',border:'1px solid rgba(201,168,76,0.3)'}} onClick={()=>startEdit(b)}>✏️ Edit</button>
              <button style={css.btnGray} onClick={()=>del(b.id)}>🗑</button>
            </div>
          </div>
        </div>
      ))}
      {items.length===0&&!loading&&<div style={{textAlign:'center',color:'#6b7280',padding:'40px',fontFamily:"'Crimson Pro',serif",fontSize:'1.05rem'}}>No branches yet. Add your first branch! ⛪</div>}
    </div>
  );
}


// ── UPI ENABLE/DISABLE TOGGLE ──
function UPIToggle() {
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    supabase.from('church_settings').select('value').eq('key','give_upi_enabled').single()
      .then(({ data }) => { if (data) setEnabled(data.value !== 'false'); });
  }, []);

  const toggle = async () => {
    const newVal = !enabled;
    setSaving(true);
    setEnabled(newVal);
    await supabase.from('church_settings').upsert({ key:'give_upi_enabled', value:String(newVal) });
    setSaving(false);
  };

  return (
    <div style={{ background: enabled ? '#f0fff0' : '#fff8e7', border:`1px solid ${enabled?'#5cb85c':'#f59e0b'}`, borderRadius:12, padding:'16px 20px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
      <div>
        <div style={{ fontFamily:"'Cinzel Decorative',serif", fontSize:'0.82rem', color: enabled?'#1d5c1d':'#166534', marginBottom:4 }}>
          📱 UPI Payment — {enabled ? 'ENABLED ✅' : 'DISABLED ⏳'}
        </div>
        <div style={{ fontSize:'0.76rem', color:'#6b7280' }}>
          {enabled ? 'UPI QR code is visible on website.' : 'Showing "Coming Soon" message. Enable once UPI is ready.'}
        </div>
      </div>
      <div onClick={toggle} style={{
        width:56, height:30, borderRadius:15, cursor: saving?'not-allowed':'pointer',
        background: enabled ? '#5cb85c' : '#ccc',
        position:'relative', transition:'background 0.25s', flexShrink:0, opacity: saving?0.7:1,
      }}>
        <div style={{ position:'absolute', top:5, left: enabled?30:5, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left 0.25s', boxShadow:'0 2px 4px rgba(0,0,0,0.2)' }} />
      </div>
    </div>
  );
}

// ─── GIVE TAB ───
function GiveTab() {
  const [form,setForm]=useState({upi_id:'',account_name:'',account_number:'',ifsc_code:'',bank_name:'',branch_name:''}); const [saving,setSaving]=useState(false); const [saved,setSaved]=useState(false); const [id,setId]=useState(null);
  useEffect(()=>{const f=async()=>{const{data}=await supabase.from('give_details').select('*').single();if(data){setForm({upi_id:data.upi_id||'',account_name:data.account_name||'',account_number:data.account_number||'',ifsc_code:data.ifsc_code||'',bank_name:data.bank_name||'',branch_name:data.branch_name||''});setId(data.id);}};f();},[]);
  const save=async()=>{setSaving(true);if(id){await supabase.from('give_details').update({...form,updated_at:new Date().toISOString()}).eq('id',id);}else{const{data}=await supabase.from('give_details').insert([form]).select().single();if(data)setId(data.id);}setSaving(false);setSaved(true);setTimeout(()=>setSaved(false),3000);};
  return (
    <div>
      <div style={{background:'rgba(34, 197, 94, 0.08)',border:'1px solid rgba(34, 197, 94, 0.2)',borderRadius:10,padding:'12px 16px',marginBottom:24,fontSize:'0.78rem',color:'#6b7280'}}>
        💡 Update your UPI ID and bank details here. Changes appear on the website immediately after saving.
      </div>
      {saved&&<div style={{background:'rgba(45,122,45,0.2)',border:'1px solid #5cb85c',borderRadius:10,padding:'12px 16px',marginBottom:16,color:'#5cb85c',fontSize:'0.88rem'}}>✅ Details saved successfully!</div>}
      <div style={{background:'#fff',border:'1px solid rgba(201,168,76,0.18)',borderRadius:14,padding:'28px 24px'}}>
        <UPIToggle />
        <h3 style={css.sectionTitle}>💳 UPI & Bank Details</h3>
        <div style={{marginBottom:14}}><label style={css.label}>UPI ID</label><input value={form.upi_id} onChange={e=>setForm(f=>({...f,upi_id:e.target.value}))} style={css.input} placeholder="e.g. zionag.ooty@upi"/></div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
          <div><label style={css.label}>Account Name</label><input value={form.account_name} onChange={e=>setForm(f=>({...f,account_name:e.target.value}))} style={css.input} placeholder="Account holder name"/></div>
          <div><label style={css.label}>Account Number</label><input value={form.account_number} onChange={e=>setForm(f=>({...f,account_number:e.target.value}))} style={css.input} placeholder="Account number"/></div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:18}}>
          <div><label style={css.label}>IFSC Code</label><input value={form.ifsc_code} onChange={e=>setForm(f=>({...f,ifsc_code:e.target.value}))} style={css.input} placeholder="IFSC code"/></div>
          <div><label style={css.label}>Bank Name</label><input value={form.bank_name} onChange={e=>setForm(f=>({...f,bank_name:e.target.value}))} style={css.input} placeholder="Bank name"/></div>
        </div>
        <div style={{marginBottom:18}}><label style={css.label}>Branch</label><input value={form.branch_name} onChange={e=>setForm(f=>({...f,branch_name:e.target.value}))} style={css.input} placeholder="Branch name"/></div>
        <button style={css.btnGold} onClick={save} disabled={saving}>{saving?'⏳ Saving…':'💾 Save Details'}</button>
      </div>
    </div>
  );
}

// ─── MAIN ADMIN ───
const TABS = [
  {key:'dashboard',label:'📊 Dashboard',component:DashboardTab},
  {key:'testimonials',label:'✝ Testimonials',component:TestimonialsTab},
  {key:'prayers',label:'🙏 Prayers',component:PrayerTab},
  {key:'gallery',label:'🖼️ Gallery',component:GalleryTab},
  {key:'verses',label:'📖 Daily Verse',component:VerseTab},
  {key:'events',label:'📅 Events',component:EventsTab},
  {key:'sermons',label:'🎤 Sermons',component:SermonsTab},
  {key:'announcements',label:'📢 Announcements',component:AnnouncementsTab},
  {key:'pastors',label:'👨‍💼 Pastors',component:PastorsTab},
  {key:'branches',label:'⛪ Branches',component:BranchesTab},
  {key:'give',label:'💳 Give/UPI',component:GiveTab},
  {key:'herovideo',label:'🎬 Hero Video',component:HeroVideoTab},
  {key:'services',label:'🕐 Services',component:ServicesTab},
  {key:'social',label:'📱 Social Media',component:SocialTab},
  {key:'themes',label:'🎨 Themes',component:ThemesTab},
  {key:'sections',label:'👁️ Sections',component:SectionsTab},
  {key:'contact',label:'📞 Contact',component:ContactTab},
  {key:'versepost',label:'📸 Verse Post',component:VersePostTab},
];

const PP = "'Poppins','Nunito','Raleway',sans-serif";

export default function Admin() {
  const [loggedIn,   setLoggedIn]   = useState(false);
  const [loggedAdmin,setLoggedAdmin] = useState('Admin');
  const [tab,        setTab]        = useState('dashboard');
  const [expanded,   setExpanded]   = useState('');

  useEffect(() => {
    if (!document.getElementById('poppins-font')) {
      const l = document.createElement('link');
      l.id='poppins-font'; l.rel='stylesheet';
      l.href='https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('zion_admin_name', loggedAdmin);
  }, [loggedAdmin]);

  if (!loggedIn) return (
    <LoginScreen onLogin={(name) => {
      const n = name || 'Admin';
      setLoggedIn(true);
      setLoggedAdmin(n);
      localStorage.setItem('zion_admin_name', n);
    }}/>
  );

  const ActiveTab = TABS.find(t => t.key === tab)?.component || DashboardTab;
  const isDash = tab === 'dashboard';

  const NAV_GROUPS = [
    { icon:'📋', label:'Content',  keys:['verses','events','sermons','announcements','gallery'] },
    { icon:'👥', label:'People',   keys:['testimonials','prayers','pastors'] },
    { icon:'⛪', label:'Church',   keys:['branches','give','herovideo','contact','services','social'] },
    { icon:'⚙️', label:'Settings', keys:['themes','sections'] },
  ];

  const SideBtn = ({ navKey, icon, label }) => {
    const active = tab === navKey;
    return (
      <button onClick={() => setTab(navKey)} style={{
        width:'100%', display:'flex', alignItems:'center', gap:11,
        padding:'10px 14px', borderRadius:10, border:'none', cursor:'pointer',
        marginBottom:2, textAlign:'left', fontFamily:PP, fontWeight:600,
        fontSize:'0.8rem', transition:'all 0.15s',
        background: active ? '#84CC16' : 'transparent',
        color:       active ? '#fff'   : '#64748B',
        boxShadow:   active ? '0 4px 14px rgba(132,204,22,0.4)' : 'none',
      }}>
        <span style={{ fontSize:'1rem', lineHeight:1 }}>{icon}</span>
        {label}
      </button>
    );
  };

  const SideGroup = ({ icon, label, keys }) => {
    const childActive = keys.includes(tab);
    const open = expanded === label || childActive;
    return (
      <div style={{ marginBottom:2 }}>
        <button onClick={() => setExpanded(open && !childActive ? '' : label)} style={{
          width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'10px 14px', borderRadius:10, border:'none', cursor:'pointer',
          fontFamily:PP, fontWeight:600, fontSize:'0.8rem', transition:'all 0.15s',
          background: childActive ? '#F0FDF4' : 'transparent',
          color:       childActive ? '#16A34A' : '#64748B',
        }}>
          <span style={{ display:'flex', alignItems:'center', gap:11 }}>
            <span style={{ fontSize:'1rem' }}>{icon}</span>{label}
          </span>
          <span style={{ fontSize:'0.55rem', opacity:0.5, transition:'transform 0.2s', display:'inline-block', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
        </button>
        {open && (
          <div style={{ paddingLeft:18, marginTop:1 }}>
            {keys.map(k => {
              const t = TABS.find(t => t.key === k);
              if (!t) return null;
              const active = tab === k;
              return (
                <button key={k} onClick={() => setTab(k)} style={{
                  width:'100%', display:'flex', alignItems:'center', gap:8,
                  padding:'8px 12px', borderRadius:8, border:'none', cursor:'pointer',
                  marginBottom:1, fontFamily:PP, fontSize:'0.74rem', textAlign:'left',
                  background: active ? '#84CC16' : 'transparent',
                  color:       active ? '#fff'   : '#94A3B8',
                  fontWeight:  active ? 700      : 500,
                  transition:'all 0.12s',
                }}>
                  <span style={{ width:5, height:5, borderRadius:'50%', background: active ? '#fff' : '#CBD5E1', flexShrink:0, display:'inline-block' }}/>
                  {t.label.replace(/[^\w\s]/g,'').trim()}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F8FAFC', fontFamily:PP }}>
      <style>{`
        *{box-sizing:border-box;}
        ::-webkit-scrollbar{width:4px;height:4px;}
        ::-webkit-scrollbar-thumb{background:#E2E8F0;border-radius:10px;}
        @media(max-width:767px){
          .admin-sidebar{display:none !important;}
          .admin-mobile-nav{display:flex !important;}
          .admin-main-pad{padding:16px !important;}
        }
      `}</style>

      {/* ══ SIDEBAR ══ */}
      <aside className="admin-sidebar" style={{
        width:224, minHeight:'100vh', background:'#fff',
        borderRight:'1px solid #F1F5F9', display:'flex', flexDirection:'column',
        position:'sticky', top:0, height:'100vh', overflowY:'auto', flexShrink:0,
        zIndex:50,
      }}>
        {/* Logo */}
        <div style={{ padding:'20px 16px 16px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#84CC16,#4D7C0F)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'1.1rem', fontWeight:900, flexShrink:0 }}>✝</div>
            <div>
              <div style={{ fontFamily:PP, fontWeight:800, fontSize:'0.9rem', color:'#1E293B', lineHeight:1.1 }}>Zion AG</div>
              <div style={{ fontFamily:PP, fontSize:'0.57rem', color:'#94A3B8', fontWeight:600, letterSpacing:'0.1em' }}>CHURCH ADMIN</div>
            </div>
          </div>
        </div>

        {/* Nav items */}
        <nav style={{ flex:1, padding:'4px 8px', overflowY:'auto' }}>
          <SideBtn navKey="dashboard" icon="▣"  label="Dashboard" />
          <SideBtn navKey="versepost" icon="📸" label="Verse Post" />
          {NAV_GROUPS.map(g => <SideGroup key={g.label} {...g}/>)}
        </nav>

        {/* Bottom profile */}
        <div style={{ padding:'12px 12px 16px', borderTop:'1px solid #F1F5F9' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:'linear-gradient(135deg,#84CC16,#4D7C0F)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'0.95rem', flexShrink:0 }}>✝</div>
            <div style={{ minWidth:0 }}>
              <div style={{ fontFamily:PP, fontWeight:700, fontSize:'0.78rem', color:'#1E293B', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{loggedAdmin}</div>
              <div style={{ fontFamily:PP, fontSize:'0.6rem', color:'#94A3B8', fontWeight:500 }}>Church Admin</div>
            </div>
          </div>
          <button onClick={() => setLoggedIn(false)} style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', fontFamily:PP, fontWeight:600, fontSize:'0.72rem', color:'#EF4444', padding:'4px 2px', marginBottom:2 }}>
            ↩ Log Out
          </button>
          <a href="/" style={{ display:'flex', alignItems:'center', gap:6, textDecoration:'none', fontFamily:PP, fontWeight:600, fontSize:'0.72rem', color:'#94A3B8', padding:'4px 2px' }}>
            ← Back to Site
          </a>
        </div>
      </aside>

      {/* ══ MAIN AREA ══ */}
      <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', overflowY:'auto' }}>

        {/* Top bar (hidden on dashboard — it has its own) */}
        {!isDash && (
          <div style={{ background:'#fff', borderBottom:'1px solid #F1F5F9', padding:'14px 28px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, position:'sticky', top:0, zIndex:40 }}>
            <div>
              <div style={{ fontFamily:PP, fontWeight:700, fontSize:'1.05rem', color:'#1E293B' }}>
                {TABS.find(t=>t.key===tab)?.label?.replace(/^[^\w]*/,'').trim() || ''}
              </div>
              <div style={{ fontFamily:PP, fontSize:'0.7rem', color:'#94A3B8', marginTop:2 }}>
                {new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
              </div>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <div style={{ width:36, height:36, borderRadius:9, background:'#F8FAFC', border:'1px solid #F1F5F9', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.95rem', cursor:'pointer' }}>🔔</div>
              <div style={{ width:36, height:36, borderRadius:9, background:'#F8FAFC', border:'1px solid #F1F5F9', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.95rem', cursor:'pointer' }}>⚙</div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="admin-main-pad" style={{ flex:1, padding: isDash ? 0 : '24px 28px' }}>
          <ActiveTab />
        </div>
      </div>

      {/* ══ MOBILE BOTTOM NAV ══ */}
      <div className="admin-mobile-nav" style={{ display:'none', position:'fixed', bottom:0, left:0, right:0, background:'#fff', borderTop:'1px solid #F1F5F9', zIndex:200, boxShadow:'0 -2px 12px rgba(0,0,0,0.06)' }}>
        {[
          { key:'dashboard',    icon:'▣',  label:'Home'     },
          { key:'versepost',    icon:'📸', label:'Post'     },
          { key:'verses',       icon:'📖', label:'Verses'   },
          { key:'testimonials', icon:'✝',  label:'People'   },
          { key:'themes',       icon:'⚙️', label:'Settings' },
        ].map(item => (
          <button key={item.key} onClick={() => setTab(item.key)} style={{
            flex:1, padding:'10px 4px', border:'none',
            background: tab===item.key ? '#F0FDF4' : 'transparent',
            borderTop:`2px solid ${tab===item.key ? '#84CC16' : 'transparent'}`,
            color: tab===item.key ? '#16A34A' : '#94A3B8',
            fontSize:'0.53rem', fontWeight:700, cursor:'pointer',
            display:'flex', flexDirection:'column', alignItems:'center', gap:3, fontFamily:PP,
          }}>
            <span style={{ fontSize:'1.15rem' }}>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── HERO VIDEO TAB ───
function HeroVideoTab() {
  const [uploading, setUploading] = useState({desktop:false, mobile:false});
  const [msg, setMsg] = useState({desktop:'', mobile:''});
  const [urls, setUrls] = useState({desktop:'', mobile:''});
  const [ts, setTs] = useState(Date.now());

  const refresh = () => {
    setTs(Date.now());
    setMsg({desktop:'', mobile:''});
  };

  React.useEffect(() => {
    setUrls({
      desktop: supabase.storage.from('gallery').getPublicUrl('hero-video.mp4').data.publicUrl,
      mobile: supabase.storage.from('gallery').getPublicUrl('hero-video-mobile.mp4').data.publicUrl,
    });
  }, [ts]);

  const upload = async (type, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) { setMsg(m=>({...m,[type]:'❌ Please select a video file'})); return; }
    if (file.size > 150 * 1024 * 1024) { setMsg(m=>({...m,[type]:'❌ Video must be under 150MB'})); return; }
    setUploading(u=>({...u,[type]:true}));
    setMsg(m=>({...m,[type]:'⏳ Uploading... please wait'}));
    const filename = type === 'desktop' ? 'hero-video.mp4' : 'hero-video-mobile.mp4';
    const { error } = await supabase.storage.from('gallery').upload(filename, file, { upsert: true, contentType: file.type });
    if (error) { setMsg(m=>({...m,[type]:'❌ Upload failed: ' + error.message})); }
    else { setMsg(m=>({...m,[type]:'✅ Uploaded! Click Refresh Preview to see.'})); setTs(Date.now()); }
    setUploading(u=>({...u,[type]:false}));
    e.target.value = '';
  };

  const VideoCard = ({type, label, hint}) => (
    <div style={{ background:'#fff', border:'1px solid #e8d8b0', borderRadius:14, padding:'24px', marginBottom:16, boxShadow:'0 2px 12px rgba(0,0,0,0.06)' }}>
      <h3 style={{ fontFamily:"'Cinzel Decorative',serif", fontSize:'0.88rem', color:'#166534', marginBottom:8 }}>{label}</h3>
      <p style={{ fontSize:'0.78rem', color:'#9ca3af', marginBottom:16, lineHeight:1.5 }}>{hint}</p>
      {msg[type] && (
        <div style={{ background: msg[type].startsWith('✅') ? '#f0fff0' : msg[type].startsWith('⏳') ? '#fffbf0' : '#fff0f0', border:`1px solid ${msg[type].startsWith('✅')?'#5cb85c':msg[type].startsWith('⏳')?'#f59e0b':'#ff6b6b'}`, borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:'0.82rem', color:msg[type].startsWith('✅')?'#2d7a2d':msg[type].startsWith('⏳')?'#166534':'#cc0000' }}>
          {msg[type]}
        </div>
      )}
      <label htmlFor={`heroVideoInput_${type}`} style={{ background:'linear-gradient(135deg,#f59e0b,#8b6914)', border:'none', color:'#ffffff', padding:'11px 22px', borderRadius:9, fontSize:'0.78rem', fontWeight:700, cursor:uploading[type]?'not-allowed':'pointer', letterSpacing:'0.08em', fontFamily:'Tahoma,Arial,sans-serif', opacity:uploading[type]?0.7:1, display:'inline-block' }}>
        {uploading[type] ? '⏳ Uploading…' : '📤 Upload Video'}
      </label>
      <input id={`heroVideoInput_${type}`} type="file" accept="video/mp4,video/*" style={{display:'none'}} onChange={e=>upload(type,e)} disabled={uploading[type]} />
      {urls[type] && (
        <div style={{ marginTop:16 }}>
          <div style={{ fontSize:'0.68rem', color:'#9ca3af', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:8 }}>Preview</div>
          <video key={`${type}-${ts}`} src={`${urls[type]}?t=${ts}`} controls style={{ width:'100%', maxWidth: type==='desktop'?500:240, borderRadius:10, border:'1px solid #e8d8b0', display:'block' }} />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <p style={{ fontSize:'0.82rem', color:'#6b7280', lineHeight:1.5 }}>
          Upload separate videos for <strong>desktop</strong> (landscape) and <strong>mobile</strong> (portrait) for best experience.
        </p>
        <button onClick={refresh} style={{ background:'#f0ebe0', border:'1px solid #d4b896', color:'#6b7280', padding:'9px 18px', borderRadius:8, cursor:'pointer', fontSize:'0.78rem', fontWeight:700, fontFamily:'Tahoma,Arial,sans-serif' }}>
          🔄 Refresh Preview
        </button>
      </div>
      <VideoCard type="desktop" label="🖥️ Desktop Video (Landscape)" hint="Shown on computers and tablets. MP4, landscape (16:9), under 150MB." />
      <VideoCard type="mobile" label="📱 Mobile Video (Portrait)" hint="Shown on phones. MP4, portrait (9:16), under 150MB. Optional — desktop video used if not uploaded." />
    </div>
  );
}
